/**
 * Inline stroke-icon set for the landing page.
 *
 * Kept local to the home feature so the marketing page carries no
 * icon-font dependency. All icons share a 24×24 grid, 1.6 stroke,
 * and inherit `currentColor`.
 */
import type { ReactNode, SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

function Base({ children, ...props }: IconProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {children}
    </svg>
  );
}

export const IconArrowRight = (p: IconProps) => (
  <Base {...p}>
    <path d="M5 12h14" />
    <path d="m13 5 7 7-7 7" />
  </Base>
);

export const IconChevronRight = (p: IconProps) => (
  <Base {...p}>
    <path d="m9 5 7 7-7 7" />
  </Base>
);

export const IconSearch = (p: IconProps) => (
  <Base {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20.5 20.5-4.3-4.3" />
  </Base>
);

export const IconSparkle = (p: IconProps) => (
  <Base {...p}>
    <path d="M12 3.5c.4 4.3 1.7 5.6 6 6-4.3.4-5.6 1.7-6 6-.4-4.3-1.7-5.6-6-6 4.3-.4 5.6-1.7 6-6Z" />
    <path d="M19 14.5c.15 1.6.65 2.1 2.25 2.25-1.6.15-2.1.65-2.25 2.25-.15-1.6-.65-2.1-2.25-2.25 1.6-.15 2.1-.65 2.25-2.25Z" />
  </Base>
);

export const IconCheck = (p: IconProps) => (
  <Base {...p}>
    <path d="m5 12.5 4.5 4.5L19 6.5" />
  </Base>
);

export const IconPlus = (p: IconProps) => (
  <Base {...p}>
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </Base>
);

export const IconUser = (p: IconProps) => (
  <Base {...p}>
    <circle cx="12" cy="8.5" r="3.6" />
    <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
  </Base>
);

export const IconBookOpen = (p: IconProps) => (
  <Base {...p}>
    <path d="M12 6.2C12 5 10.6 4.2 7.8 4.2 5 4.2 3.5 5 3.5 5v13.5s1.5-.8 4.3-.8 4.2.8 4.2.8m0-13.3V19m0-12.8C12 5 13.4 4.2 16.2 4.2 19 4.2 20.5 5 20.5 5v13.5s-1.5-.8-4.3-.8-4.2.8-4.2.8" />
  </Base>
);

export const IconListChoose = (p: IconProps) => (
  <Base {...p}>
    <path d="M9 6h11" />
    <path d="M9 12h11" />
    <path d="M9 18h11" />
    <path d="M4 6h.01" />
    <path d="M4 12h.01" />
    <path d="M4 18h.01" />
  </Base>
);

export const IconLightbulb = (p: IconProps) => (
  <Base {...p}>
    <path d="M9.5 17.5h5" />
    <path d="M10.5 21h3" />
    <path d="M12 2.5a6.5 6.5 0 0 0-3.9 11.7c.6.5.9 1.1.9 1.8v.5h6v-.5c0-.7.3-1.3.9-1.8A6.5 6.5 0 0 0 12 2.5Z" />
  </Base>
);

export const IconRepeat = (p: IconProps) => (
  <Base {...p}>
    <path d="M17 2.5 21 6.5l-4 4" />
    <path d="M3 12.5v-2a4 4 0 0 1 4-4h14" />
    <path d="M7 21.5 3 17.5l4-4" />
    <path d="M21 11.5v2a4 4 0 0 1-4 4H3" />
  </Base>
);

export const IconRefresh = (p: IconProps) => (
  <Base {...p}>
    <path d="M21 12a9 9 0 1 1-2.64-6.36" />
    <path d="M21 3v5h-5" />
  </Base>
);

export const IconAward = (p: IconProps) => (
  <Base {...p}>
    <circle cx="12" cy="9" r="6" />
    <path d="M8.2 13.6 7 22l5-2.6L17 22l-1.2-8.4" />
  </Base>
);

export const IconFocus = (p: IconProps) => (
  <Base {...p}>
    <path d="M4 8V5.5A1.5 1.5 0 0 1 5.5 4H8" />
    <path d="M16 4h2.5A1.5 1.5 0 0 1 20 5.5V8" />
    <path d="M20 16v2.5a1.5 1.5 0 0 1-1.5 1.5H16" />
    <path d="M8 20H5.5A1.5 1.5 0 0 1 4 18.5V16" />
    <circle cx="12" cy="12" r="2.4" />
  </Base>
);

export const IconShield = (p: IconProps) => (
  <Base {...p}>
    <path d="M12 3 5 5.8v5.4c0 4.6 2.9 7.8 7 9.8 4.1-2 7-5.2 7-9.8V5.8L12 3Z" />
    <path d="m9 11.8 2 2 4-4" />
  </Base>
);

export const IconLock = (p: IconProps) => (
  <Base {...p}>
    <rect x="4.5" y="10.5" width="15" height="10" rx="2" />
    <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
  </Base>
);

export const IconKey = (p: IconProps) => (
  <Base {...p}>
    <circle cx="8" cy="15.5" r="4.5" />
    <path d="m11.2 12.3 8.3-8.3" />
    <path d="m16 5 3 3" />
    <path d="m13.6 7.4 3 3" />
  </Base>
);

export const IconMail = (p: IconProps) => (
  <Base {...p}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3.5 7 8.5 6 8.5-6" />
  </Base>
);

export const IconClock = (p: IconProps) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5V12l3.2 2" />
  </Base>
);

export const IconBookmark = (p: IconProps) => (
  <Base {...p}>
    <path d="M6 3.5h12v17l-6-4-6 4v-17Z" />
  </Base>
);

export const IconMore = (p: IconProps) => (
  <Base {...p}>
    <circle cx="12" cy="5.5" r="1.3" />
    <circle cx="12" cy="12" r="1.3" />
    <circle cx="12" cy="18.5" r="1.3" />
  </Base>
);

export const IconCopy = (p: IconProps) => (
  <Base {...p}>
    <rect x="8" y="8" width="12" height="12" rx="2" />
    <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
  </Base>
);

export const IconShare = (p: IconProps) => (
  <Base {...p}>
    <circle cx="18" cy="5.5" r="2.5" />
    <circle cx="6" cy="12" r="2.5" />
    <circle cx="18" cy="18.5" r="2.5" />
    <path d="m8.2 10.8 7.6-4" />
    <path d="m8.2 13.2 7.6 4" />
  </Base>
);

export const IconPlay = (p: IconProps) => (
  <Base {...p}>
    <path d="M7.5 5.5v13l11-6.5-11-6.5Z" />
  </Base>
);

export const IconPause = (p: IconProps) => (
  <Base {...p}>
    <path d="M9 5v14" />
    <path d="M15 5v14" />
  </Base>
);

export const IconVolume = (p: IconProps) => (
  <Base {...p}>
    <path d="M5 9.5h3l5-4.5v14l-5-4.5H5v-5Z" />
    <path d="M16 8.5a4 4 0 0 1 0 7" />
  </Base>
);

export const IconMic = (p: IconProps) => (
  <Base {...p}>
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0" />
    <path d="M12 18v3" />
  </Base>
);

export const IconCheckCircle = (p: IconProps) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="m8.4 12 2.6 2.6 4.6-5" />
  </Base>
);

export const IconReplay10 = (p: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...p}
  >
    <path d="M12 4V2L7.5 5.5 12 9V7a6 6 0 1 1-6 6" />
    <text
      x="13"
      y="18"
      fontSize="6.5"
      fontWeight="700"
      textAnchor="middle"
      fill="currentColor"
      stroke="none"
    >
      10
    </text>
  </svg>
);

export const IconCamera = (p: IconProps) => (
  <Base {...p}>
    <path d="M4 8.5A1.5 1.5 0 0 1 5.5 7H8l1.4-2h5.2L16 7h2.5A1.5 1.5 0 0 1 20 8.5V18a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18V8.5Z" />
    <circle cx="12" cy="13" r="3.2" />
  </Base>
);

export const IconClose = (p: IconProps) => (
  <Base {...p}>
    <path d="m6 6 12 12" />
    <path d="M18 6 6 18" />
  </Base>
);

export const IconUpload = (p: IconProps) => (
  <Base {...p}>
    <path d="M21 15v3.5A1.5 1.5 0 0 1 19.5 20h-15A1.5 1.5 0 0 1 3 18.5V15" />
    <path d="M16.5 8.5 12 4 7.5 8.5" />
    <path d="M12 4v12" />
  </Base>
);

export const IconForward10 = (p: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...p}
  >
    <path d="M12 4V2l4.5 3.5L12 9V7a6 6 0 1 0 6 6" />
    <text
      x="11"
      y="18"
      fontSize="6.5"
      fontWeight="700"
      textAnchor="middle"
      fill="currentColor"
      stroke="none"
    >
      10
    </text>
  </svg>
);

export const IconSun = (p: IconProps) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2" />
    <path d="M12 20v2" />
    <path d="m4.9 4.9 1.4 1.4" />
    <path d="m17.7 17.7 1.4 1.4" />
    <path d="M2 12h2" />
    <path d="M20 12h2" />
    <path d="m4.9 19.1 1.4-1.4" />
    <path d="m17.7 6.3 1.4-1.4" />
  </Base>
);

export const IconMoon = (p: IconProps) => (
  <Base {...p}>
    <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
  </Base>
);

export const IconLogIn = (p: IconProps) => (
  <Base {...p}>
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
    <path d="M10 17l5-5-5-5" />
    <path d="M15 12H3" />
  </Base>
);

export const IconLogOut = (p: IconProps) => (
  <Base {...p}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="m16 17 5-5-5-5" />
    <path d="M21 12H9" />
  </Base>
);

export const IconHome = (p: IconProps) => (
  <Base {...p}>
    {/* Roof + walls */}
    <path d="M3 11 12 4l9 7" />
    <path d="M5 10v9a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1v-9" />
  </Base>
);

export const IconChalkboard = (p: IconProps) => (
  <Base {...p}>
    {/* Board */}
    <rect x="3" y="4" width="18" height="13" rx="1.5" />
    {/* Pointer / text-line on the board */}
    <path d="M7 9h6" />
    <path d="M7 13h10" />
    {/* Stand */}
    <path d="M9 20l3-3 3 3" />
  </Base>
);

export const IconGraduationCap = (p: IconProps) => (
  <Base {...p}>
    {/* Mortarboard top — flat square plate tilted in perspective */}
    <path d="M2 9 12 5l10 4-10 4L2 9Z" />
    {/* Tassel hanging off the right edge */}
    <path d="M22 9v5" />
    <path d="M22 14a1 1 0 0 0-1 1v1" />
    {/* Cap base under the board (the bit that hugs the head) */}
    <path d="M6 11v4a6 6 0 0 0 12 0v-4" />
  </Base>
);

export const IconFlame = (p: IconProps) => (
  <Base {...p}>
    <path d="M12 2c.5 3.5 3.5 5.5 3.5 9a3.5 3.5 0 0 1-7 0c0-1.6.8-2.5.8-2.5S10 11 9 13c-1 2-3 3-3 6a6 6 0 0 0 12 0c0-4-3-6-6-17Z" />
  </Base>
);

export const IconStar = (p: IconProps) => (
  <Base {...p}>
    <path d="m12 3 2.6 5.6 6.1.6-4.6 4.2 1.3 6L12 16.8 6.6 19.4l1.3-6L3.3 9.2l6.1-.6L12 3Z" />
  </Base>
);

export const IconCalendar = (p: IconProps) => (
  <Base {...p}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 9h18" />
    <path d="M8 3v4" />
    <path d="M16 3v4" />
  </Base>
);

export const IconTrendUp = (p: IconProps) => (
  <Base {...p}>
    <path d="m3 17 6-6 4 4 8-8" />
    <path d="M14 7h7v7" />
  </Base>
);

// Teacher / Qari icon — a centered person bust above an open book.
// Drawn at the same 24×24 viewBox + 1.5px stroke weight as the other
// nav icons (IconGraduationCap, IconBookOpen) so it visually matches
// them in scale and weight when rendered at h-5 w-5.
export const IconTeacher = (p: IconProps) => (
  <Base {...p}>
    {/* Head — centered horizontally for visual balance */}
    <circle cx="12" cy="5" r="2.25" />
    {/* Shoulders / yoke — symmetric U-shape closing under the head */}
    <path d="M6.5 13v-1a5.5 5.5 0 0 1 11 0v1" />
    {/* Open book held in front of the body (V-shape with pages) */}
    <path d="M4 15.5l8 1.5 8-1.5v4.5l-8-1.5-8 1.5z" />
    {/* Book spine — vertical line down the middle of the pages */}
    <path d="M12 17v3" />
  </Base>
);

// Auto-scroll icon — stacked "double chevron down" reads as "scroll
// forward". Used in the SurahReader auto-scroll control (toolbar pill
// + mobile floating button).
export const IconAutoScroll = (p: IconProps) => (
  <Base {...p}>
    <path d="m7 6 5 5 5-5" />
    <path d="m7 13 5 5 5-5" />
  </Base>
);
